import { useCallback, useContext, useMemo, type ReactElement, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import type { FlightsClassTypes } from 'constants/flight'
import { FLEXIBILITY_INCLUSIONS } from 'constants/flight'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineDollarSignIcon from 'LuxKit/Icons/line/LineDollarSignIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import ModalContext from 'contexts/ModalContext'
import FlightFareRulesModal from 'components/Common/Modals/FlightFareRulesModal/FlightFareRulesModal'
import noop from 'lib/function/noop'
import { hasNoFlexibilityFareFeatures } from 'lib/flights/flightUtils'
import { sortBy, take } from 'lib/array/arrayUtils'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import useCharterDateFareFlightFareFamilies from 'hooks/CharterFlights/useCharterDateFareFlightFareFamilies'
import MaldivesCharterFareFeatureDetailsSkeleton from './MaldivesCharterFareFeatureDetailsSkeleton'

const statusToIcon = new Map<App.FareFamilyFeatureStatus, ReactElement>([
  // oxlint-disable-next-line react/jsx-key
  ['CHARGED', <LineDollarSignIcon colour="highlight-primary-dark" />],
  // oxlint-disable-next-line react/jsx-key
  ['INCLUDED', <LineCheckIcon colour="highlight-primary-dark" />],
  // oxlint-disable-next-line react/jsx-key
  ['NOT_OFFERED', <LineTimesIcon />],
])

const MAX_EXTRA_ITEMS = 2
const MIN_HIDDEN_ITEMS_FOR_TOGGLE = 2

const CellPad = styled.div`
  padding: ${rem(12)};
`

interface Props {
  fareClass: FlightsClassTypes
  checkIn: string
  checkOut: string
  rooms: Array<App.Occupants>
  section: 'baggage' | 'extra'
  /** Used when `section` is `extra` so every cabin row toggles the same expanded "extras" list together. */
  extraFeaturesExpanded?: boolean
  onToggleExtraFeatures?: () => void
}

function MaldivesCharterTableExpandedFareFeatureCell(props: Props) {
  const {
    fareClass,
    checkIn,
    checkOut,
    rooms,
    section,
    extraFeaturesExpanded = false,
    onToggleExtraFeatures = noop,
  } = props

  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const {
    departingFareFamily,
    features,
    fetchingFareFeatures,
    fareFeaturesError,
    fareFeaturesFetchSuccess,
    fareFeaturesFetchEnabled,
  } = useCharterDateFareFlightFareFamilies({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkIn,
    checkOut,
    occupancies: rooms,
    fareClass,
  })

  const categoryKey = section === 'baggage' ? 'baggage' : 'extra'

  const sectionRows = useMemo(() => {
    return sortBy(
      (features ?? []).filter(f => f.category === categoryKey),
      f => f.group,
      'asc',
    )
  }, [categoryKey, features])

  const formatFeatureDescription = useCallback((feature: App.FareFamilyFeature) => {
    if (feature.type && FLEXIBILITY_INCLUSIONS.has(feature.type)) {
      return intl.formatMessage(
        {
          id: 'cXGc_LQLcCqiKn1rw0CQ',
          defaultMessage: '{description} *',
          description: 'Fare feature description with asterisk indicating flexibility inclusion',
        },
        { description: feature.description },
      )
    }
    return feature.description
  }, [intl])

  const onShowFareRules = useCallback((url: string) => {
    if (!url) {
      return
    }
    const maybePromise = showModal(<FlightFareRulesModal urls={[url]} />)
    if (maybePromise && typeof (maybePromise as Promise<unknown>).catch === 'function') {
      (maybePromise as Promise<unknown>).catch(noop)
    }
  }, [showModal])

  const onViewFareRules = useCallback(() => {
    if (departingFareFamily?.fareRulesUrl) {
      onShowFareRules(departingFareFamily.fareRulesUrl)
    }
  }, [departingFareFamily, onShowFareRules])

  const fareDetailsLoadErrorLabel = intl.formatMessage({
    id: 'EeLZd4RtpChRnnVKPYXE',
    defaultMessage: 'We couldn’t load fare inclusions. Try again in a moment.',
    description:
      'Inline error message under the Maldives charter calendar fare card when the fare upsell request for baggage and cabin rows fails so the customer knows the comparison block did not load.',
  })

  const fareDetailsEmptyLabel = intl.formatMessage({
    id: 'z0sTpoxOGjbSDIAI_xaL',
    defaultMessage: 'Fare inclusions aren’t available for this cabin on these dates.',
    description:
      'Helper copy shown in the expanded Maldives charter fare card when the upsell response succeeded but no matching fare family was returned for the selected cabin and trip dates.',
  })

  const fareDetailsUnavailableLabel = intl.formatMessage({
    id: 'z9WF1hsm8DsldyNCbyOQ',
    defaultMessage: 'Fare inclusions will appear once matching flights are available for this stay.',
    description:
      'Helper copy in the expanded Maldives charter fare card when flight search results are not yet matched to the selected check-in and check-out dates so baggage and cabin rows cannot load yet.',
  })

  const showLessLabel = intl.formatMessage({
    id: '2_-wo1GfttsJOv6p_CUf',
    defaultMessage: 'Show less',
    description: 'Hyperlink text to collapse extra fare features in the flights section',
  })

  const showMoreLabel = intl.formatMessage({
    id: 'Q-NkmmNfa1Y3i5CgGSY-',
    defaultMessage: 'Show more',
    description: 'Hyperlink text to expand extra fare features in the flights section',
  })

  const hasResolvedFamily = Boolean(departingFareFamily && !fareFeaturesError)

  const showFareDetailsSkeleton = !fareFeaturesError
    && !departingFareFamily
    && (fetchingFareFeatures || (fareFeaturesFetchEnabled && !fareFeaturesFetchSuccess))

  const showFareDetailsEmpty = !fareFeaturesError
    && fareFeaturesFetchEnabled
    && fareFeaturesFetchSuccess
    && !departingFareFamily
    && !fetchingFareFeatures

  const showFareDetailsUnavailable = !fareFeaturesError
    && !fareFeaturesFetchEnabled
    && !departingFareFamily
    && !fetchingFareFeatures

  const hiddenItemsCount = section === 'extra' ? sectionRows.length - MAX_EXTRA_ITEMS : 0
  const shouldShowToggle = section === 'extra' && hiddenItemsCount >= MIN_HIDDEN_ITEMS_FOR_TOGGLE

  const visibleExtraRows = section === 'extra' && shouldShowToggle && !extraFeaturesExpanded
    ? take(sectionRows, MAX_EXTRA_ITEMS)
    : sectionRows

  const showFlexibilityFooter = section === 'extra'
    && hasResolvedFamily
    && hasNoFlexibilityFareFeatures(features ?? [])

  const renderFeatureRows = (rows: Array<App.FareFamilyFeature>) => (
    <VerticalSpacer gap={8}>
      {rows.map(row => (
        <BodyTextBlock
          variant="small"
          key={row.code}
          startIcon={statusToIcon.get(row.status)}
          colour={row.status === 'NOT_OFFERED' ? 'neutral-four' : undefined}
        >
          {formatFeatureDescription(row)}
        </BodyTextBlock>
      ))}
    </VerticalSpacer>
  )

  return (
    <CellPad>
      {fareFeaturesError && (
        <Caption variant="medium" colour="critical">
          {fareDetailsLoadErrorLabel}
        </Caption>
      )}
      {showFareDetailsSkeleton && (
        <MaldivesCharterFareFeatureDetailsSkeleton />
      )}
      {showFareDetailsEmpty && (
        <Caption variant="medium" colour="neutral-three">
          {fareDetailsEmptyLabel}
        </Caption>
      )}
      {showFareDetailsUnavailable && (
        <Caption variant="medium" colour="neutral-three">
          {fareDetailsUnavailableLabel}
        </Caption>
      )}
      {hasResolvedFamily && section === 'baggage' && sectionRows.length > 0 && renderFeatureRows(sectionRows)}
      {hasResolvedFamily && section === 'baggage' && sectionRows.length === 0 && (
        <BodyText variant="small" colour="neutral-three">
          {fareDetailsEmptyLabel}
        </BodyText>
      )}
      {hasResolvedFamily && section === 'extra' && sectionRows.length > 0 && (
        <VerticalSpacer gap={8}>
          {renderFeatureRows(visibleExtraRows)}
          {shouldShowToggle && (
            <TextLink
              onClick={onToggleExtraFeatures}
              endIcon={extraFeaturesExpanded ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
              size="caption"
            >
              {extraFeaturesExpanded ? showLessLabel : showMoreLabel}
            </TextLink>
          )}
          {showFlexibilityFooter && (
            <BodyTextBlock colour="neutral-three" variant="small">
              <FormattedMessage
                id="BwnEZaenmkHhScFCS2u-"
                defaultMessage="View <link>fare rules</link> for changes, cancellation & refunds policy *"
                description="Prompt to view fare rules for changes, cancellation and refunds policy"
                values={{
                  link: (chunks: ReactNode) => (
                    <TextLink key="fare-rules-inline" size="small" weight="regular" onClick={onViewFareRules}>
                      {chunks}
                    </TextLink>
                  ),
                }}
              />
            </BodyTextBlock>
          )}
        </VerticalSpacer>
      )}
      {hasResolvedFamily && section === 'extra' && sectionRows.length === 0 && (
        <BodyText variant="small" colour="neutral-three">
          {fareDetailsEmptyLabel}
        </BodyText>
      )}
    </CellPad>
  )
}

export default MaldivesCharterTableExpandedFareFeatureCell
